import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Tabs, Tab, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, LinearProgress } from '@mui/material';
import { statisticsAPI } from '../services/api';

const Statistics = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [criticalComplaints, setCriticalComplaints] = useState([]);
  const [overdueComplaints, setOverdueComplaints] = useState([]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const fetchStatisticsData = async () => {
      try {
        setLoading(true);
        const [overview, critical, overdue] = await Promise.all([
          statisticsAPI.getOverview(),
          statisticsAPI.getCriticalComplaints(),
          statisticsAPI.getOverdueComplaints()
        ]);

        if (overview.data.success) {
          setOverviewData(overview.data.data);
        }

        // Handle double-nested structure for critical complaints
        if (critical.data.success) {
          const critData = critical.data.data.rows?.rows || critical.data.data.rows || [];
          setCriticalComplaints(Array.isArray(critData) ? critData : []);
        }

        // Handle double-nested structure for overdue complaints
        if (overdue.data.success) {
          const overdueData = overdue.data.data.rows?.rows || overdue.data.data.rows || [];
          setOverdueComplaints(Array.isArray(overdueData) ? overdueData : []);
        }
      } catch (err) {
        setError('Error loading statistics: ' + err.message);
        console.error('Statistics error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStatisticsData();
  }, []);

  const criticalMetrics = overviewData ? [
    { label: 'Critical Count', value: criticalComplaints.length.toString(), status: 'High', color: '#ef4444' },
    { label: 'Overdue (SLA)', value: overdueComplaints.length.toString(), status: 'Alert', color: '#f97316' },
    { label: 'Avg Days Open', value: overviewData.avg_age_days + 'd', status: 'Normal', color: '#10b981' },
    { label: 'Satisfaction', value: overviewData.satisfaction?.average_rating + '/5', status: 'Good', color: '#667eea' }
  ] : [
    { label: 'Critical Complaints', value: '0', status: 'Loading', color: '#ef4444' },
    { label: 'Overdue (SLA)', value: '0', status: 'Loading', color: '#f97316' },
    { label: 'Average Response', value: '0d', status: 'Loading', color: '#10b981' },
    { label: 'Satisfaction Score', value: '0/5', status: 'Loading', color: '#667eea' }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header Section */}
      <Box sx={{ mb: 2, px: 1 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#1f2937',
            mb: 1
          }}
        >
          Statistics
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#6b7280',
            fontSize: '0.95rem'
          }}
        >
          Key metrics, critical alerts, and performance indicators
        </Typography>
      </Box>

      {/* Critical Metrics Grid */}
      <Grid container spacing={2} sx={{ mb: 3, px: 1 }}>
        {criticalMetrics.map((metric, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                bgcolor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: 2,
                borderTop: `3px solid ${metric.color}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: metric.color,
                  boxShadow: `0 4px 12px ${metric.color}22`
                }
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#9ca3af',
                  fontWeight: 500,
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>{metric.label}</span>
                <Box
                  component="span"
                  sx={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    bgcolor: `${metric.color}20`,
                    color: metric.color
                  }}
                >
                  {metric.status}
                </Box>
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: metric.color
                }}
              >
                {metric.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Detailed Statistics Tabs */}
      <Paper elevation={0} sx={{ bgcolor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 2, mx: 0 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: `1px solid #e5e7eb`,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
              color: '#6b7280',
              padding: '12px 20px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                color: '#667eea',
                bgcolor: '#f9fafb'
              }
            },
            '& .MuiTab-root.Mui-selected': {
              color: '#667eea',
              fontWeight: 600
            },
            '& .MuiTabIndicator-root': {
              backgroundColor: '#667eea',
              height: 2
            }
          }}
        >
          <Tab label="Overview" />
          <Tab label="Critical Complaints" />
          <Tab label="Overdue Alerts" />
          <Tab label="Satisfaction" />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ p: 2, px: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error" sx={{ p: 3, textAlign: 'center' }}>{error}</Typography>
          ) : (
            <>
              {/* Overview Tab */}
              {tabValue === 0 && overviewData && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 2, bgcolor: '#f0f9ff', borderLeft: '4px solid #667eea' }}>
                        <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>Total Complaints</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#667eea', mt: 1 }}>
                          {overviewData.total_complaints}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 2, bgcolor: '#f0fdf4', borderLeft: '4px solid #10b981' }}>
                        <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>Resolved</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981', mt: 1 }}>
                          {overviewData.resolved} <Typography component="span" sx={{ fontSize: '0.9rem', color: '#9ca3af' }}>
                            ({overviewData.resolution_rate}%)
                          </Typography>
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 2, bgcolor: '#fef3c7', borderLeft: '4px solid #f59e0b' }}>
                        <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>In Progress</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#f59e0b', mt: 1 }}>
                          {overviewData.in_progress}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 2, bgcolor: '#fee2e2', borderLeft: '4px solid #ef4444' }}>
                        <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>Deferred</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#ef4444', mt: 1 }}>
                          {overviewData.deferred}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Paper sx={{ p: 3, mt: 3, bgcolor: '#ffffff' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Satisfaction Metrics</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                          Overall Rating: <strong>{overviewData.satisfaction?.average_rating}/5</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          Satisfaction Rate: <strong>{overviewData.satisfaction?.satisfaction_percentage}%</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Satisfied ({overviewData.satisfaction?.satisfied})</Typography>
                          <LinearProgress
                            variant="determinate"
                            value={(overviewData.satisfaction?.satisfied / (overviewData.satisfaction?.rated_total || 1)) * 100}
                            sx={{ mt: 0.5, '& .MuiLinearProgress-bar': { backgroundColor: '#10b981' } }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Unsatisfied ({overviewData.satisfaction?.unsatisfied})</Typography>
                          <LinearProgress
                            variant="determinate"
                            value={(overviewData.satisfaction?.unsatisfied / (overviewData.satisfaction?.rated_total || 1)) * 100}
                            sx={{ mt: 0.5, '& .MuiLinearProgress-bar': { backgroundColor: '#ef4444' } }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              )}

              {/* Critical Complaints Tab */}
              {tabValue === 1 && (
                <Box sx={{ overflowX: 'auto' }}>
                  <Typography sx={{ mb: 2, fontWeight: 600 }}>Open Critical Complaints ({criticalComplaints.length})</Typography>
                  {criticalComplaints.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Tracking ID</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Complainant</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Days Open</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Feedback</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {criticalComplaints.slice(0, 20).map((complaint, idx) => (
                          <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                            <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem' }}>{complaint.tracking_id}</TableCell>
                            <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {complaint.subject}
                            </TableCell>
                            <TableCell>{complaint.complainant_name}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ color: complaint.days_open > 30 ? '#ef4444' : '#f59e0b', fontWeight: 600 }}>
                                {complaint.days_open}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{complaint.feedback_count || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f9fafb' }}>
                      <Typography variant="body2" sx={{ color: '#9ca3af' }}>No critical complaints found</Typography>
                    </Paper>
                  )}
                </Box>
              )}

              {/* Overdue Alerts Tab */}
              {tabValue === 2 && (
                <Box sx={{ overflowX: 'auto' }}>
                  <Typography sx={{ mb: 2, fontWeight: 600 }}>Overdue Complaints ({overdueComplaints.length})</Typography>
                  {overdueComplaints.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Tracking ID</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Complainant</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>District</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#ef4444' }}>Days Open</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {overdueComplaints.slice(0, 20).map((complaint, idx) => (
                          <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                            <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem' }}>{complaint.tracking_id}</TableCell>
                            <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {complaint.subject}
                            </TableCell>
                            <TableCell>{complaint.complainant_name}</TableCell>
                            <TableCell>{complaint.district}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ color: '#ef4444', fontWeight: 700, fontSize: '1rem' }}>
                                {complaint.days_open}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f9fafb' }}>
                      <Typography variant="body2" sx={{ color: '#9ca3af' }}>No overdue complaints found</Typography>
                    </Paper>
                  )}
                </Box>
              )}

              {/* Satisfaction Tab */}
              {tabValue === 3 && overviewData && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 3, bgcolor: '#f0fdf4', textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981' }}>
                          {overviewData.satisfaction?.satisfied || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280', mt: 1 }}>Satisfied Users</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 3, bgcolor: '#f3f3f3', textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#6b7280' }}>
                          {overviewData.satisfaction?.neutral || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280', mt: 1 }}>Neutral</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 3, bgcolor: '#fee2e2', textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#ef4444' }}>
                          {overviewData.satisfaction?.unsatisfied || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280', mt: 1 }}>Unsatisfied Users</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 3, bgcolor: '#f0f9ff', textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#667eea' }}>
                          {overviewData.satisfaction?.average_rating}/5
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280', mt: 1 }}>Avg Rating</Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Paper sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Satisfaction Distribution</Typography>
                    <Box>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>Satisfied</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
                            {((overviewData.satisfaction?.satisfied / (overviewData.satisfaction?.rated_total || 1)) * 100).toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(overviewData.satisfaction?.satisfied / (overviewData.satisfaction?.rated_total || 1)) * 100}
                          sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: '#10b981' } }}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>Neutral</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#6b7280' }}>
                            {((overviewData.satisfaction?.neutral / (overviewData.satisfaction?.rated_total || 1)) * 100).toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(overviewData.satisfaction?.neutral / (overviewData.satisfaction?.rated_total || 1)) * 100}
                          sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: '#6b7280' } }}
                        />
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>Unsatisfied</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#ef4444' }}>
                            {((overviewData.satisfaction?.unsatisfied / (overviewData.satisfaction?.rated_total || 1)) * 100).toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(overviewData.satisfaction?.unsatisfied / (overviewData.satisfaction?.rated_total || 1)) * 100}
                          sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: '#ef4444' } }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Statistics;
